// Studio team Inc 3 — consume + send the cohost (team) backchannel in a room.
//
// SECURITY: viewers are remote participants of the SAME LiveKit room, so "team
// only" is enforced at the SFU, not the UI. send() addresses the packet to
// publisher identities only (destinationIdentities), so viewers never receive
// the bytes at transport. The receive-side origin gate (participant is a
// publisher) is defense-in-depth — it can't be spoofed because the SFU stamps
// the authenticated permissions on every packet. permissions can be transiently
// undefined right after join, so we FAIL CLOSED: undefined = not a publisher
// (never accepted, never addressed).
//
// Mirrors useSocialChat: topic → origin → shape → dedupe → bounded ring buffer.

import {
  COHOST_CHAT_TOPIC,
  decodeCohostChat,
  encodeCohostChat,
  type CohostChatMessage,
} from '@archetelos/lib/live';
import { RoomEvent } from 'livekit-client';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { Participant, Room } from 'livekit-client';

const DEFAULT_BUFFER_SIZE = 200;

/** The single publisher test, used for BOTH the origin gate and addressing.
 *  Fail closed: undefined permissions = not a publisher. */
function isPublisher(p: Participant | undefined): boolean {
  return p?.permissions?.canPublish === true;
}

export interface UseCohostChatResult {
  messages: CohostChatMessage[];
  send: (body: string) => void;
}

export function useCohostChat(
  room: Room | null,
  opts: { bufferSize?: number } = {},
): UseCohostChatResult {
  const bufferSize = opts.bufferSize ?? DEFAULT_BUFFER_SIZE;
  const [messages, setMessages] = useState<CohostChatMessage[]>([]);
  const seenRef = useRef<Set<string>>(new Set());

  const append = useCallback(
    (msg: CohostChatMessage) => {
      const seen = seenRef.current;
      if (seen.has(msg.id)) return; // dedupe (covers the local-echo belt-and-suspenders)
      seen.add(msg.id);
      if (seen.size > bufferSize * 4) {
        const oldest = seen.values().next().value;
        if (oldest !== undefined) seen.delete(oldest);
      }
      setMessages((prev) =>
        prev.length >= bufferSize
          ? [...prev.slice(prev.length - bufferSize + 1), msg]
          : [...prev, msg],
      );
    },
    [bufferSize],
  );

  useEffect(() => {
    if (!room) return;
    const onData = (
      payload: Uint8Array,
      participant?: Participant,
      _kind?: unknown,
      topic?: string,
    ): void => {
      if (topic !== COHOST_CHAT_TOPIC) return; // topic gate
      if (!isPublisher(participant)) return; // origin gate (fail closed)
      const msg = decodeCohostChat(payload); // shape gate
      if (!msg) return;
      append(msg);
    };
    room.on(RoomEvent.DataReceived, onData);
    return () => {
      room.off(RoomEvent.DataReceived, onData);
    };
  }, [room, append]);

  const send = useCallback(
    (body: string): void => {
      const trimmed = body.trim();
      if (!trimmed || !room) return;
      const local = room.localParticipant;
      const msg: CohostChatMessage = {
        id: crypto.randomUUID(),
        senderId: local.identity,
        senderName: local.name || local.identity,
        body: trimmed,
        sentAt: Date.now(),
      };
      // Address ONLY publishers (host + cohosts) + self. Viewers — same-room
      // remote participants with canPublish=false — are excluded at the SFU.
      const publishers = [...room.remoteParticipants.values()]
        .filter(isPublisher)
        .map((p) => p.identity);
      const destinationIdentities = [local.identity, ...publishers];
      void local.publishData(encodeCohostChat(msg), {
        topic: COHOST_CHAT_TOPIC,
        reliable: true,
        destinationIdentities,
      });
      // LiveKit doesn't loop your own data back, so echo locally.
      append(msg);
    },
    [room, append],
  );

  return { messages, send };
}
